import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { AuthService } from '@instaclause-webapp/shared-core';
import {
  ButtonComponent,
  EmailFieldComponent,
  ToastComponent,
  ToastService,
  ToastType,
} from '@instaclause-webapp/shared-ui';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { catchError, firstValueFrom, of } from 'rxjs';

import { MenuComponent } from '../../ui/menu/menu.component';

@Component({
  selector: 'app-forgot-password',
  imports: [
    CommonModule,
    MenuComponent,
    RouterModule,
    TranslocoModule,
    ReactiveFormsModule,
    EmailFieldComponent,
    ButtonComponent,
    ToastComponent,
  ],
  templateUrl: './forgot-password.component.html',
  styleUrl: './forgot-password.component.scss',
})
export class ForgotPasswordComponent {
  isSubmitting = false;
  // The success state is identical whether or not the email exists (no user enumeration).
  submitted = signal(false);
  form = inject(FormBuilder).group({
    email: [ '', Validators.required ],
  });

  private authService = inject(AuthService);
  private toastService = inject(ToastService);
  private translocoService = inject(TranslocoService);

  async submit() {
    if (this.isSubmitting) return;

    if (!this.form.valid) {
      this.form.markAllAsTouched();

      return;
    }

    const { email } = this.form.value;
    if (!email) return;

    this.isSubmitting = true;
    try {
      const result = await firstValueFrom(
        this.authService.requestPasswordReset(email, this.translocoService.getActiveLang()).pipe(
          catchError(() => {
            this.toastService.showToast({
              type: ToastType.Error,
              text: this.translocoService.translate('LOGIN.GENERIC_ERROR'),
              duration: 5,
              testId: 'forgot-password-error-toast',
            });

            return of('failed' as const);
          })
        )
      );

      if (result !== 'failed') {
        this.submitted.set(true);
      }
    } finally {
      this.isSubmitting = false;
    }
  }
}
